/**
 * App navigation — the single source of truth for the desktop sidebar,
 * breadcrumbs and the ⌘K palette.
 *
 * Order follows the operator's workflow:
 *   Overview → Reports → Planning → Accounting → Data   (main)
 *   Chat · Admin · Help Center · Support · account menu  (footer)
 *
 * Every item may carry:
 *   key        stable id (also the collapsible open-state key)
 *   title      label
 *   icon       lucide component (top-level items only)
 *   to         link target (may include a query string)
 *   match      path prefixes that mark the item active (defaults to `to`'s path)
 *   exact      paths that match only when equal (no prefix semantics)
 *   gate(ctx)  visibility — ctx = { flags, groups, isAdmin, isStaff, hasReportGrants }
 *   action     non-route behaviour ('assistant' | 'support')
 *   children   sub-items (same shape, no icon)
 *   sidebarLeaf  render as a plain link in the sidebar even with children
 *              (children still feed breadcrumbs, ⌘K and active matching)
 *
 * Gates reproduce the rules of the previous Sidebar.js / SettingsLayout /
 * AdminTabs exactly — see navConfig.test.js before changing one.
 */
import {
  LayoutDashboard,
  Sparkles,
  Briefcase,
  BarChart3,
  Users,
  LibraryBig,
  BellRing,
  CalendarRange,
  UsersRound,
  Calculator,
  BookOpen,
  FileDown,
  Workflow,
  Network,
  MessageSquare,
  ShieldCheck,
  CircleHelp,
  User,
  Bell,
  Shield,
  Key,
  Moon,
} from 'lucide-react';
import {
  hasNativeReportGroup,
  canAccessForecasting,
  canAccessBudgeting,
} from './reportGroupAccess';
import { assistantV2Active } from '../modules/assistant/flag';

/** Every PostHog flag the nav reads — useResolvedNav calls the hook once per entry. */
export const NAV_FLAGS = [
  'ai-assistant',
  'assistant-v2',
  'ai-summary',
  'journal-export',
  'budgeting',
  'forecasting',
  'labor-budgeting',
  'revenue-map',
  'journal-map',
  'position-map',
  'vendor-master',
  'data-import',
  'import-review',
  'schema-builder',
  'night-audit-risk',
  'night-audit-config',
  'night-audit-review',
  'ai-quality',
  'admin-ops',
  'security-settings',
  'api-key-gen',
  'forecast-evolution',
];

// Flag helpers. `on` = definitely enabled (undefined while PostHog loads
// hides the item, as the old sidebar did). `notOff` = shown unless
// explicitly disabled (the routes' `!== false` convention).
const on = (flag) => (ctx) => ctx.flags[flag] === true;
const notOff = (flag) => (ctx) => ctx.flags[flag] !== false;
// Staged rollout of the Assistant (v2): the flag, in a build that has the service URL.
const assistantV2 = (ctx) => assistantV2Active(ctx.flags['assistant-v2']);
const report = (group) => (ctx) => ctx.hasReportGrants && hasNativeReportGroup(ctx.groups, group);
// Lodgeic staff only (isAdmin + @lodgeic.io), plus an optional extra gate.
const staff = (gate) => (ctx) => !!ctx.isStaff && (!gate || gate(ctx));
const dashboardSection = (group) => (ctx) => hasNativeReportGroup(ctx.groups, group);

export const NAV_MAIN = [
  {
    key: 'analytics',
    label: 'Analytics',
    items: [
      {
        key: 'dashboard',
        title: 'Dashboard',
        icon: LayoutDashboard,
        // One sidebar link straight to the overview; the dashboard's own tabs
        // reach the other sections.
        to: '/dashboard/overview',
        match: ['/dashboard'],
        sidebarLeaf: true,
        children: [
          { key: 'dashboard-overview', title: 'Overview', to: '/dashboard/overview', exact: ['/dashboard/overview'] },
          { key: 'dashboard-revenue', title: 'Revenue', to: '/dashboard/revenue', exact: ['/dashboard/revenue'], gate: dashboardSection('revenue') },
          { key: 'dashboard-operations', title: 'Operations', to: '/dashboard/operations', exact: ['/dashboard/operations'], gate: dashboardSection('operations') },
          { key: 'dashboard-financial', title: 'Financial', to: '/dashboard/financial', exact: ['/dashboard/financial'], gate: dashboardSection('financial') },
          { key: 'dashboard-forecast', title: 'Forecast', to: '/dashboard/forecast', exact: ['/dashboard/forecast'], gate: dashboardSection('revenue') },
          { key: 'dashboard-night-audit', title: 'Night Audit', to: '/dashboard/night-audit', gate: on('night-audit-risk') },
        ],
      },
      {
        // Analytics dashboard (one page across every report facet). Kept on
        // its route for breadcrumb + ⌘K; the tabbed dashboard is the sidebar
        // entry.
        key: 'analytics',
        title: 'Analytics',
        icon: LayoutDashboard,
        to: '/analytics',
        exact: ['/analytics'],
        sidebar: false,
      },
      {
        key: 'reports-revenue',
        title: 'Revenue',
        icon: BarChart3,
        to: '/reports/revenue/overview',
        match: ['/reports/revenue'],
        gate: report('revenue'),
        children: [
          { key: 'report-revenue', title: 'Revenue', to: '/reports/revenue/overview', match: ['/reports/revenue/overview', '/reports/revenue/trends'] },
          { key: 'report-revenue-forecast', title: 'Revenue Forecast', to: '/reports/revenue/forecast' },
          { key: 'report-forecast-evolution', title: 'Forecast Evolution', to: '/reports/revenue/forecast-evolution', gate: on('forecast-evolution') },
          { key: 'report-night-audits', title: 'Night Audits', to: '/reports/revenue/night-audits' },
          { key: 'report-market-performance', title: 'Market Performance', to: '/reports/revenue/market-performance' },
        ],
      },
      {
        key: 'reports-operations',
        title: 'Operations',
        icon: Users,
        to: '/reports/operations/labor',
        match: ['/reports/operations'],
        gate: report('operations'),
        children: [
          { key: 'report-labor', title: 'Labor Management', to: '/reports/operations/labor' },
          { key: 'report-reviews', title: 'Guest Reviews', to: '/reports/operations/reviews' },
          { key: 'report-night-audit-review', title: 'Night Audit Review', to: '/reports/operations/night-audit', gate: on('night-audit-review') },
        ],
      },
      {
        key: 'reports-financial',
        title: 'Financial',
        icon: Briefcase,
        to: '/reports/financial/pnl',
        match: ['/reports/financial'],
        gate: report('financial'),
        children: [
          { key: 'report-pnl', title: 'Profit + Loss', to: '/reports/financial/pnl' },
          { key: 'report-financial-trends', title: 'Trends', to: '/reports/financial/trends' },
          { key: 'report-gl-detail', title: 'GL Detail', to: '/reports/financial/gl-detail' },
        ],
      },
      {
        key: 'reports-catalog',
        title: 'All Reports',
        icon: LibraryBig,
        to: '/reports',
        exact: ['/reports'],
        // Desktop sidebar already lists every report; the catalog stays
        // reachable via ⌘K + breadcrumb (mobile has its own nav list).
        sidebar: false,
      },
    ],
  },
  {
    key: 'workspace',
    label: 'Workspace',
    items: [
      {
        key: 'lodgeic-ai',
        title: 'Lodgeic AI',
        icon: Sparkles,
        action: 'assistant',
        badge: 'New',
        // The Assistant replaces Lodgeic AI wherever it's on.
        gate: (ctx) => on('ai-assistant')(ctx) && !assistantV2(ctx),
      },
      // Same item, name and icon as Lodgeic AI: v2 takes its place.
      {
        key: 'assistant',
        title: 'Lodgeic AI',
        icon: Sparkles,
        to: '/assistant',
        badge: 'New',
        gate: assistantV2,
      },
      {
        key: 'subscriptions',
        title: 'Subscriptions + Alerts',
        icon: BellRing,
        to: '/subscriptions',
        match: ['/subscriptions'],
        children: [
          { key: 'subscriptions-reports', title: 'Report Subscriptions', to: '/subscriptions', exact: ['/subscriptions', '/subscriptions/subscriptions'] },
          { key: 'subscriptions-ai-summary', title: 'AI Summary', to: '/subscriptions/ai-summary', gate: on('ai-summary') },
          { key: 'subscriptions-ai-tasks', title: 'AI Scheduled Tasks', to: '/subscriptions/ai-tasks', match: ['/subscriptions/ai-tasks', '/workflow-runs'], gate: notOff('ai-assistant') },
          { key: 'subscriptions-alerts', title: 'Alerts', to: '/subscriptions/alerts' },
        ],
      },
    ],
  },
  {
    key: 'planning',
    label: 'Planning',
    items: [
      {
        key: 'forecasting',
        title: 'Budget + Forecast',
        icon: CalendarRange,
        to: '/forecast-v3',
        gate: (ctx) => canAccessForecasting(ctx.groups, ctx.flags.forecasting),
      },
      {
        key: 'labor-budgets',
        title: 'Labor Budgeting',
        icon: UsersRound,
        to: '/labor-budgets',
        gate: on('labor-budgeting'),
      },
      {
        key: 'budgeting',
        title: 'Budgeting (classic)',
        icon: Calculator,
        to: '/budgeting?tab=Budget',
        match: ['/budgeting'],
        // Retired from the sidebar; /budgeting still opens by URL (and keeps
        // its breadcrumb and search entry).
        sidebar: false,
        gate: (ctx) => canAccessBudgeting(ctx.groups, ctx.flags.budgeting),
      },
    ],
  },
  {
    key: 'accounting',
    label: 'Accounting',
    items: [
      {
        key: 'journal',
        title: 'Journal',
        icon: BookOpen,
        to: '/journal-export',
        match: ['/journal-export'],
        gate: on('journal-export'),
        children: [
          { key: 'journal-overview', title: 'Overview', to: '/journal-export', exact: ['/journal-export'] },
          { key: 'journal-detail', title: 'Export detail', to: '/journal-export/detail' },
        ],
      },
      {
        key: 'revenue-export',
        title: 'Revenue Export',
        icon: FileDown,
        to: '/revenue-export',
      },
    ],
  },
  {
    key: 'data',
    label: 'Data',
    items: [
      {
        key: 'integrations',
        title: 'Integrations',
        icon: Workflow,
        to: '/etl-config/status',
        match: ['/etl-config'],
        gate: on('data-import'),
        children: [
          { key: 'etl-status', title: 'Status', to: '/etl-config/status', exact: ['/etl-config', '/etl-config/status'] },
          { key: 'etl-issues', title: 'Issues', to: '/etl-config/issues' },
          { key: 'etl-documents', title: 'Documents', to: '/etl-config/documents' },
          { key: 'etl-unmapped', title: 'Unmapped', to: '/etl-config/unmapped' },
          { key: 'etl-review', title: 'Review', to: '/etl-config/review', gate: notOff('import-review') },
          { key: 'etl-feeds', title: 'Feeds', to: '/etl-config/feeds' },
          { key: 'etl-upload', title: 'Upload', to: '/etl-config/upload' },
          { key: 'etl-schema', title: 'Schema Builder', to: '/etl-config/schema', gate: notOff('schema-builder') },
          { key: 'etl-onboard', title: 'Onboard property', to: '/etl-config/onboard' },
        ],
      },
      {
        key: 'mappings',
        title: 'Mappings',
        icon: Network,
        to: '/chart-of-accounts',
        match: ['/chart-of-accounts', '/revenue-types', '/forecast-sources', '/journal-types', '/position-department-mapping', '/vendors'],
        children: [
          { key: 'map-coa', title: 'Chart of Accounts', to: '/chart-of-accounts' },
          { key: 'map-revenue', title: 'Revenue Map', to: '/revenue-types', gate: on('revenue-map') },
          { key: 'map-forecast-sources', title: 'Forecast Sources', to: '/forecast-sources', gate: on('revenue-map') },
          { key: 'map-journal', title: 'Journal Map', to: '/journal-types', gate: on('journal-map') },
          { key: 'map-position', title: 'Position Map', to: '/position-department-mapping', gate: on('position-map') },
          { key: 'map-vendors', title: 'Vendors', to: '/vendors', gate: (ctx) => ctx.flags['vendor-master'] === true || process.env.REACT_APP_VENDOR_MASTER === 'true' },
        ],
      },
    ],
  },
];

export const NAV_FOOTER = [
  {
    key: 'chat',
    title: 'Chat',
    icon: MessageSquare,
    to: '/chat',
    badgeSource: 'chat',
  },
  {
    key: 'admin',
    title: 'Admin',
    icon: ShieldCheck,
    to: '/admin/users',
    match: ['/admin'],
    gate: (ctx) => ctx.isAdmin,
    // Customer admins get Users only; every other page is platform-wide and
    // Lodgeic staff only (AdminTabs and AdminProtectedRoute follow these gates).
    children: [
      { key: 'admin-users', title: 'Users', to: '/admin/users', exact: ['/admin', '/admin/users'] },
      { key: 'admin-organizations', title: 'Organizations', to: '/admin/organizations', gate: staff(on('admin-ops')) },
      { key: 'admin-comp-sets', title: 'Comp Sets', to: '/admin/comp-sets', gate: staff() },
      // Spans every org's threads (API enforces staff too).
      { key: 'admin-ai-skills', title: 'AI Skills', to: '/admin/ai-skills', gate: staff() },
      // Evals, runs and every customer's assistant conversations: staff the
      // `ai-quality` flag names (the API enforces both; views are logged).
      { key: 'admin-ai-quality', title: 'AI Quality', to: '/admin/ai-quality', gate: staff(on('ai-quality')) },
      { key: 'admin-night-audit', title: 'Night Audit Config', to: '/admin/night-audit-config', gate: staff(on('night-audit-config')) },
    ],
  },
  {
    key: 'help',
    title: 'Help Center',
    icon: CircleHelp,
    to: '/help',
  },
];

/** Account menu (user card in the sidebar footer). */
export const NAV_SETTINGS = {
  key: 'settings',
  title: 'Settings',
  match: ['/settings'],
  children: [
    { key: 'settings-account', title: 'Account', icon: User, to: '/settings/account' },
    { key: 'settings-notifications', title: 'Notifications', icon: Bell, to: '/settings/notifications', match: ['/settings/notifications', '/settings/email-notifications'] },
    { key: 'settings-security', title: 'Security', icon: Shield, to: '/settings/security', gate: notOff('security-settings') },
    { key: 'settings-api-keys', title: 'API Keys', icon: Key, to: '/settings/api-keys', gate: on('api-key-gen') },
    { key: 'settings-appearance', title: 'Appearance', icon: Moon, to: '/settings/appearance' },
  ],
};

// ---------------------------------------------------------------------------
// Pure helpers (tested in navConfig.test.js)
// ---------------------------------------------------------------------------

const pathOf = (to) => (to ? String(to).split('?')[0] : null);

/** Does `pathname` activate `item` (ignoring children)? */
export function itemMatches(item, pathname) {
  if (!pathname) return false;
  if (item.exact) return item.exact.includes(pathname);
  const prefixes = item.match || (item.to ? [pathOf(item.to)] : []);
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

const visible = (item, ctx) => (typeof item.gate === 'function' ? !!item.gate(ctx) : true);

/**
 * Filter a list of items by their gates. A parent whose every child is gated
 * away keeps its own link (the parent route is still reachable) but loses
 * the disclosure.
 */
export function resolveItems(items, ctx) {
  return (items || [])
    .filter((item) => visible(item, ctx))
    .map((item) => {
      if (!item.children) return item;
      const children = item.children.filter((c) => visible(c, ctx));
      return { ...item, children };
    });
}

/** Resolve the whole nav for a context. */
export function resolveNav(ctx) {
  const main = NAV_MAIN
    .map((group) => ({ ...group, items: resolveItems(group.items, ctx) }))
    .filter((group) => group.items.length > 0);
  const footer = resolveItems(NAV_FOOTER, ctx);
  const [settings] = resolveItems([NAV_SETTINGS], ctx);
  return { main, footer, settings };
}

/**
 * Breadcrumb trail for `pathname` — [group label?, item, child?]. The most
 * specific child match wins; falls back to the parent item. `siblings` is
 * the list the last crumb belongs to (the item's children, or the group's
 * items), so the header can offer it as a page switcher.
 */
export function findTrail(nav, pathname) {
  const scan = (items, groupLabel) => {
    for (const item of items) {
      const child = (item.children || []).find((c) => itemMatches(c, pathname));
      if (child) return { group: groupLabel, item, child, siblings: item.children };
      if (itemMatches(item, pathname)) return { group: groupLabel, item, child: null, siblings: items };
    }
    return null;
  };
  for (const group of nav.main) {
    const hit = scan(group.items, group.label);
    if (hit) return hit;
  }
  const footerHit = scan(nav.footer, null);
  if (footerHit) return footerHit;
  if (nav.settings) {
    const hit = scan([nav.settings], null);
    if (hit) return hit;
  }
  return null;
}

/** Flat list of every navigable leaf, for the command palette. */
export function flattenNav(nav) {
  const out = [];
  const push = (item, section, parent) => {
    if (item.children && item.children.length) {
      item.children.forEach((c) => {
        if (c.to) out.push({ key: c.key, title: c.title, to: c.to, section, parent: parent || item.title });
      });
    } else if (item.to) {
      out.push({ key: item.key, title: item.title, to: item.to, section, parent: null });
    }
  };
  nav.main.forEach((g) => g.items.forEach((item) => push(item, g.label)));
  nav.footer.forEach((item) => push(item, 'Workspace'));
  if (nav.settings) push(nav.settings, 'Settings', 'Settings');
  return out;
}

/**
 * Can this user open `pathname`? Judged by the same gates as the nav — a page
 * the sidebar hides is a page the user can't use — so a link from the Help
 * Center or the assistant can land on "you don't have access" instead of a
 * silent redirect.
 *
 * Walks the full (unresolved) nav for the most specific item chain matching
 * the path. Paths the nav doesn't list are allowed. Returns
 *   { allowed: true } or
 *   { allowed: false, title, reason: 'admin' | 'grant' | 'feature' }
 * and { allowed: true, pending: true } while a flag or the report grants are
 * still loading — the route guards decide then.
 */
export function navAccess(pathname, ctx) {
  const path = pathOf(pathname);
  if (!path || !ctx) return { allowed: true };
  const chains = [];
  const scan = (items, parents) => (items || []).forEach((item) => {
    const chain = [...parents, item];
    if (itemMatches(item, path)) chains.push(chain);
    if (item.children) scan(item.children, chain);
  });
  NAV_MAIN.forEach((group) => scan(group.items, []));
  scan(NAV_FOOTER, []);
  scan([NAV_SETTINGS], []);
  if (!chains.length) return { allowed: true };
  // Most specific match: the deepest chain, then the longest matching prefix.
  const chain = chains.sort((a, b) => b.length - a.length)[0];
  const passes = (c) => chain.every((item) => visible(item, c));
  if (passes(ctx)) return { allowed: true };

  const flags = ctx.flags || {};
  const flagsLoadingOn = { ...ctx, flags: Object.fromEntries(Object.entries(flags).map(([k, v]) => [k, v === undefined ? true : v])) };
  const optimistic = { ...flagsLoadingOn, hasReportGrants: true, groups: ctx.hasReportGrants ? ctx.groups : null };
  if (passes(optimistic)) return { allowed: true, pending: true };

  // Why: Lodgeic-admin pages; a report grant an org admin can give
  // (User setup → Report Access); or a feature not turned on for the org.
  const title = chain[chain.length - 1].title;
  const everyFlag = new Proxy({}, { get: () => true });
  const everyGroup = ['financial', 'operations', 'revenue', 'forecasting'];
  let reason = 'feature';
  if (!passes({ ...ctx, flags: everyFlag, groups: everyGroup, hasReportGrants: true })) reason = 'admin';
  else if (passes({ ...flagsLoadingOn, groups: everyGroup, hasReportGrants: true })) reason = 'grant';
  return { allowed: false, title, reason };
}
